import React, { useEffect, useRef, useState } from 'react';
import { HiOutlineSpeakerphone } from 'react-icons/hi';
import {
  diaDaSemana,
  formatarData,
  obterStatusEncerramento,
  StatusEncerramento,
} from '~/config/encerramentoSistema';
import history from '~/services/history';
import ModalComunicado from './ModalComunicado';
import TelaAcessoEncerrado from './TelaAcessoEncerrado';
import {
  BotaoBarra,
  ContainerBarra,
  EstiloGlobalBarraAviso,
  IconeBarra,
  SeloContagem,
  TextoBarra,
} from './styles';

const INTERVALO_VERIFICACAO_MS = 60 * 1000;
const CHAVE_COMUNICADO_VISTO = 'KONVIX_ENCERRAMENTO_COMUNICADO_VISTO';

const lerComunicadoVisto = (): string | null => {
  try {
    return localStorage.getItem(CHAVE_COMUNICADO_VISTO);
  } catch {
    return null;
  }
};

const marcarComunicadoVisto = (hoje: string): void => {
  try {
    localStorage.setItem(CHAVE_COMUNICADO_VISTO, hoje);
  } catch {
    // sem acesso ao storage: o comunicado volta a abrir no próximo carregamento
  }
};

const textoContagem = (diasAteUltimoDia: number): string => {
  if (diasAteUltimoDia === 0) return 'Hoje é o último dia';
  if (diasAteUltimoDia === 1) return 'Amanhã é o último dia';
  return `Faltam ${diasAteUltimoDia} dias`;
};

interface BarraAvisoProps {
  ultimoDiaDeAcesso: string;
  diasAteUltimoDia: number;
  onVerComunicado(): void;
}

const BarraAviso: React.FC<BarraAvisoProps> = ({
  ultimoDiaDeAcesso,
  diasAteUltimoDia,
  onVerComunicado,
}) => (
  <>
    <EstiloGlobalBarraAviso />
    <ContainerBarra role="status" aria-live="polite">
      <IconeBarra>
        <HiOutlineSpeakerphone size={18} aria-hidden />
      </IconeBarra>
      <TextoBarra>
        <span className="texto-completo">
          <strong>Comunicado importante:</strong> o acesso a este sistema será
          encerrado. Último dia de acesso:{' '}
          <strong>
            {diaDaSemana(ultimoDiaDeAcesso)}, {formatarData(ultimoDiaDeAcesso)}
          </strong>
        </span>
        <span className="texto-curto">
          <span className="prefixo-curto">
            <strong>Encerramento do sistema</strong> ·{' '}
          </span>
          Último dia de acesso:{' '}
          <strong>{formatarData(ultimoDiaDeAcesso)}</strong>
        </span>
      </TextoBarra>
      <SeloContagem urgente={diasAteUltimoDia <= 2}>
        {textoContagem(diasAteUltimoDia)}
      </SeloContagem>
      <BotaoBarra type="button" onClick={onVerComunicado}>
        Ver comunicado
      </BotaoBarra>
    </ContainerBarra>
  </>
);

/**
 * Aviso de encerramento do sistema e bloqueio após o prazo, conforme
 * `~/config/encerramentoSistema`. Para hosts não configurados, apenas renderiza
 * os filhos.
 */
const EncerramentoSistema: React.FC = ({ children }) => {
  const [status, setStatus] = useState<StatusEncerramento>(
    obterStatusEncerramento,
  );
  const [comunicadoAberto, setComunicadoAberto] = useState(false);
  const situacaoAnterior = useRef(status.situacao);

  useEffect(() => {
    const atualizar = () => {
      const novoStatus = obterStatusEncerramento();
      setStatus((atual) =>
        JSON.stringify(atual) === JSON.stringify(novoStatus)
          ? atual
          : novoStatus,
      );
    };

    const intervalo = window.setInterval(atualizar, INTERVALO_VERIFICACAO_MS);
    const pararDeOuvirRotas = history.listen(atualizar);
    document.addEventListener('visibilitychange', atualizar);

    return () => {
      window.clearInterval(intervalo);
      pararDeOuvirRotas();
      document.removeEventListener('visibilitychange', atualizar);
    };
  }, []);

  // Prazo encerrado com o sistema aberto: recarrega para que o useAuth limpe a
  // sessão e a tela de encerramento seja exibida desde o início.
  useEffect(() => {
    if (
      status.situacao === 'encerrado' &&
      situacaoAnterior.current !== 'encerrado'
    ) {
      window.onbeforeunload = null;
      window.location.replace('/');
    }
    situacaoAnterior.current = status.situacao;
  }, [status.situacao]);

  const aviso = status.situacao === 'aviso' ? status : null;
  const hoje = aviso?.hoje;

  // Abre o comunicado automaticamente uma vez por dia. No PDV online ele não
  // abre sozinho (a tela já abre modais próprios), mas a barra continua visível.
  useEffect(() => {
    if (!hoje || history.location.pathname.startsWith('/pdv-online')) return;
    if (lerComunicadoVisto() === hoje) return;

    marcarComunicadoVisto(hoje);
    setComunicadoAberto(true);
  }, [hoje]);

  if (status.situacao === 'encerrado') {
    return <TelaAcessoEncerrado ultimoDiaDeAcesso={status.ultimoDiaDeAcesso} />;
  }

  // Mantém os filhos sempre na mesma posição para não remontar as rotas
  // quando o aviso aparece ou some.
  return (
    <>
      {aviso && (
        <BarraAviso
          ultimoDiaDeAcesso={aviso.ultimoDiaDeAcesso}
          diasAteUltimoDia={aviso.diasAteUltimoDia}
          onVerComunicado={() => setComunicadoAberto(true)}
        />
      )}
      {children}
      {aviso && (
        <ModalComunicado
          show={comunicadoAberto}
          onClose={() => setComunicadoAberto(false)}
          ultimoDiaDeAcesso={aviso.ultimoDiaDeAcesso}
          diasAteUltimoDia={aviso.diasAteUltimoDia}
        />
      )}
    </>
  );
};

export default EncerramentoSistema;
